import Preact from 'preact';
import * as Hooks from 'preact/hooks';
import SceneComponent from 'babylonjs-hook';
import * as BABYLON from '@babylonjs/core';
import '@babylonjs/loaders';
import makeStyles from '@mui/styles/makeStyles';
import * as O from 'fp-ts/Option';
import {
    createArcRotateCamera,
    createGround,
    createWall,
    createGuiLayer,
    createInteractionHint,
    createLights,
    createPBRSkybox,
} from './sceneInitialisation';
import * as ConfigurationActions from '../../store/actions/configuration';
import { generateGltf, generateModelJson, ParticleTreeNode, updateViewer } from './furniture';
import { useAddCommand } from '../hooks/useConsoleCommand';
import { ModelRequest } from '../ar';
import { useDispatch } from '../hooks/redux';
import { useFeatureFlag } from '../hooks/useFeatureFlag';
import * as RenderError from '../rendererError';
import { AnyAction, Dispatch } from 'redux';
import { TranslateContext } from '@denysvuika/preact-translate';
import { logError } from '../../store/actions/logging';

export function Component(props: {
    particleTree: ParticleTreeNode | undefined;
    attachmentSelections: { [tag: string]: string };
    onSelectAttachmentOption: (attachmentTag: string, value: string) => void;
    setGenerateGltf: (getGltf: () => () => O.Option<Promise<Blob>>) => void;
    setGenerateModelRequest: (getMr: () => () => O.Option<ModelRequest>) => void;
    setModelLoading: (loading: boolean) => void;
    setSceneReadyForSnapshot: (ready: boolean) => void;
    setSceneError: (error: boolean) => void;
    setSnapshot: (getSnapshot: () => () => Promise<string>) => void;
}) {
    const showCameraInteractionHint = useFeatureFlag('camera-interaction-hint');
    const panToModelOnSelection = useFeatureFlag('camera-pan-to-model-on-selection');
    let [logFurniture, setLogFurniture] = Hooks.useState<boolean>(false);
    const [scene, setScene] = Hooks.useState<BABYLON.Scene>();
    const [shadowGenerator, setShadowGenerator] = Hooks.useState<BABYLON.ShadowGenerator>();
    const [apUtilLayer, setApUtilLayer] = Hooks.useState<BABYLON.UtilityLayerRenderer>();
    const [renderError, setRenderError] = Hooks.useState(false);

    const shadowSurfaces = Hooks.useRef<{ ground: BABYLON.Mesh | null; wall: BABYLON.Mesh | null }>(
        { ground: null, wall: null },
    );

    // Loading states - one for fetching the model data from the network and another for completing the viewer update (inc. animations etc)
    const [modelLoading, setModelLoading] = Hooks.useState<boolean>(true);
    const [viewerLoading, setViewerLoading] = Hooks.useState<boolean>(true);

    const dispatch = useDispatch();
    const { t } = Hooks.useContext(TranslateContext);

    const onSelectAttachmentOptionRef = Hooks.useRef<
        (attachmentTag: string, value: string) => void
    >(() => {});
    Hooks.useEffect(() => {
        onSelectAttachmentOptionRef.current = props.onSelectAttachmentOption;
    }, [props.onSelectAttachmentOption]);

    useAddCommand(
        'enableAttachmentsDebug',
        () => {
            setLogFurniture(true);
        },
        [scene],
    );

    useAddCommand(
        'showSceneDebugger',
        () => {
            import('@babylonjs/inspector').then(() => {
                scene?.debugLayer.show({
                    embedMode: true,
                });
            });
        },
        [scene],
    );

    // init scene on babylon load
    const onSceneReady = (scene: BABYLON.Scene) => {
        const canvas = scene.getEngine().getRenderingCanvas();
        if (canvas) {
            // prevent touch events bubbling up to the browser - this stops weird behaviour like being able to select the canvas for copy / paste in safari
            canvas.addEventListener('touchstart', evt => evt.preventDefault());
            canvas.addEventListener('touchmove', evt => evt.preventDefault());
            canvas.addEventListener('touchend', evt => evt.preventDefault());
            canvas.addEventListener('touchcancel', evt => evt.preventDefault());

            // True while in the initial camera interaction hint loop
            let runningCameraHint = false;

            const camera = createArcRotateCamera(
                canvas,
                scene,
                () => {
                    if (runningCameraHint) return;
                    dispatch(ConfigurationActions.user3dSceneInteractionAction());
                },
                panToModelOnSelection,
            );

            if (showCameraInteractionHint) {
                // TODO: Tidy camera interaction hint experiment setup (TDC-1118)
                runningCameraHint = true;
                const gui = createGuiLayer(scene);

                const cameraInteractionHint = createInteractionHint(scene, gui, camera);

                const cameraHintInterval = setInterval(() => cameraInteractionHint.play(), 10000);

                const dismissCameraHint = () => {
                    runningCameraHint = false;
                    clearInterval(cameraHintInterval);
                    cameraInteractionHint.cancel();
                    canvas.removeEventListener('pointerdown', dismissCameraHint);
                };
                canvas.addEventListener('pointerdown', dismissCameraHint);
            }

            const startAlpha = Math.PI / 2;
            const startBeta = Math.PI / 2.6;
            const startRadius = 1.45;
            const startPosition = new BABYLON.Vector3(0, 0.5, 0);
            const snapshotCamera = new BABYLON.ArcRotateCamera(
                'snapshotCamera',
                startAlpha,
                startBeta,
                startRadius,
                startPosition,
                scene,
                true,
            );

            createPBRSkybox(scene);
            const shadowGenerator = createLights(scene);
            setShadowGenerator(shadowGenerator);
            shadowSurfaces.current.ground = createGround(scene);
            shadowSurfaces.current.wall = createWall(scene);
            setScene(scene);
            const apLayer = new BABYLON.UtilityLayerRenderer(scene);
            apLayer.utilityLayerScene.attachControl();
            setApUtilLayer(apLayer);
            apLayer.utilityLayerScene.onPointerPick = (event, pickingInfo) => {
                if (
                    pickingInfo?.hit &&
                    pickingInfo.pickedMesh &&
                    event.button === 0 &&
                    pickingInfo.pickedMesh.name === 'attachmentPointSelectionSphere'
                ) {
                    const [selectedOptionKey, selectedValue] = pickingInfo.pickedMesh.id.split(':');

                    onSelectAttachmentOptionRef.current(selectedOptionKey, selectedValue);
                }
            };
        }
    };

    Hooks.useEffect(() => {
        props.setModelLoading(modelLoading);
    }, [modelLoading]);

    Hooks.useEffect(() => {
        props.setSceneError(renderError);
    }, [renderError]);

    const [updatesInQueue, setUpdatesInQueue] = Hooks.useState(0);

    Hooks.useEffect(() => {
        if (updatesInQueue === 0) {
            props.setSceneReadyForSnapshot(true);
        } else {
            props.setSceneReadyForSnapshot(false);
        }
    }, [updatesInQueue]);

    Hooks.useEffect(() => {
        if (scene && shadowGenerator && props.particleTree && apUtilLayer) {
            updateViewerWithLoadingStates(
                setModelLoading,
                setViewerLoading,
                scene,
                apUtilLayer,
                shadowGenerator,
                props.particleTree,
                logFurniture,
                shadowSurfaces.current,
                setRenderError,
                dispatch,
                setUpdatesInQueue,
            );
        }
    }, [scene, shadowGenerator, props.particleTree, apUtilLayer]);

    Hooks.useEffect(() => {
        const snapshotCamera = scene?.getCameraByName('snapshotCamera');
        if (scene && snapshotCamera) {
            props.setSnapshot(() => () => {
                const prevColor = scene.clearColor;
                scene.shadowsEnabled = false;

                // wait for the scene to be ready after removing shadows
                // otherwise the first call of this will result in a blank screenshot
                return scene.whenReadyAsync().then(() => {
                    // changing background colour after waiting otherwise it is a visible flash
                    scene.clearColor = new BABYLON.Color4(0, 0, 0, 0);
                    const snapshot = BABYLON.Tools.CreateScreenshotUsingRenderTargetAsync(
                        scene.getEngine(),
                        snapshotCamera,
                        { width: 415, height: 245, precision: 2 },
                    );
                    scene.clearColor = prevColor;
                    scene.shadowsEnabled = true;

                    return snapshot;
                });
            });
        }
    }, [scene]);

    // set the canvas width/height
    const styles = makeStyles({
        canvas: {
            width: '100%',
            height: '100%',
            outline: 'none',
        },
    });

    Hooks.useEffect(() => {
        if (!viewerLoading && !!scene) {
            props.setGenerateGltf(() => () => O.fromNullable(generateGltf(scene)));
            props.setGenerateModelRequest(() => () => O.fromNullable(generateModelJson(scene)));
        }
    }, [scene, viewerLoading]);

    return (
        <>
            <SceneComponent
                antialias
                onSceneReady={onSceneReady}
                id="configuration-canvas"
                className={styles().canvas}
                observeCanvasResize={false}
            />
            {renderError === true && (
                <RenderError.Component
                    heading={t('i18n.configuration.rendererErrorHeading')}
                    paragraph={t('i18n.configuration.rendererErrorBody')}
                />
            )}
        </>
    );
}

/**
 * Update the viewer and handle the loading state for the application
 * @param setModelLoading used to denote the model is downloading from the network
 * @param setViewerLoading used to denote the model is being added and animated in the viewer
 * @param scene reference to add tree to
 * @param apUtilLayer reference to add hotspots to
 * @param shadowGenerator reference to add models to shadow maps
 * @param particleTree the tree node to add
 * @param shouldLogFurniture whether or not to output debugging information relating to the constructed tree
 */
async function updateViewerWithLoadingStates(
    setModelLoading: (isModelLoading: boolean) => void,
    setViewerLoading: (isViewerLoading: boolean) => void,
    scene: BABYLON.Scene,
    apUtilLayer: BABYLON.UtilityLayerRenderer,
    shadowGenerator: BABYLON.ShadowGenerator,
    particleTree: ParticleTreeNode,
    shouldLogFurniture: boolean,
    shadowSurfaces: { ground: BABYLON.Mesh | null; wall: BABYLON.Mesh | null },
    setRendererError: (error: boolean) => void,
    dispatch: Dispatch<AnyAction>,
    setUpdatesInQueue: Hooks.StateUpdater<number>,
) {
    setModelLoading(true);
    setViewerLoading(true);
    setRendererError(false);
    setUpdatesInQueue(prev => prev + 1);

    const { animationComplete } = await updateViewer(
        scene,
        shadowGenerator,
        apUtilLayer,
        particleTree,
        shouldLogFurniture,
        shadowSurfaces,
        errPayload => {
            setRendererError(true);
            dispatch(logError(errPayload.reason, errPayload));
            dispatch(ConfigurationActions.errorAction(errPayload));
        },
    );
    setModelLoading(false);

    await animationComplete;
    setViewerLoading(false);
    setUpdatesInQueue(prev => prev - 1);
}
